import {
  areaList,
  areaName,
  ROLE_CODES,
  type Paginated,
  type PermissionDef,
  type RoleCode,
  type RoleMatrix,
  type SsoMappingInput,
  type SsoMappingView,
  type UserListItem,
} from '@ftthnms/shared';
import { Badge, Button, EmptyState, RegionChip } from '@ftthnms/ui';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Lock, Pencil, Plus, RotateCcw, Search, Trash2, UserX, Users } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field, Pager, RequireAbility, Switch, Tabs } from '../components/common';
import { useToast } from '../components/Toast';
import { api, ApiError, dateTime, qs, send } from '../lib/api';
import { ROLE_LABEL, useSession } from '../lib/session';

type Tab = 'users' | 'mappings' | 'matrix';

/** Pengguna & Peran (AUTH-07, SET-07): users, SSO role mapping and the permission matrix. Admin only. */
export function UsersPage() {
  const [tab, setTab] = useState<Tab>('users');
  return (
    <RequireAbility action="manage" subject="User">
      <div className="fn-page-body">
        <Tabs
          tabs={[
            ['users', 'Pengguna'],
            ['mappings', 'Pemetaan role SSO'],
            ['matrix', 'Hak akses per peran'],
          ]}
          value={tab}
          onChange={setTab}
        />
        {tab === 'users' && <UsersTab />}
        {tab === 'mappings' && <MappingsTab />}
        {tab === 'matrix' && <MatrixTab />}
      </div>
    </RequireAbility>
  );
}

// ------------------------------------------------------------------ users

function UsersTab() {
  const { me } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [role, setRole] = useState('');
  const [active, setActive] = useState('');
  const [page, setPage] = useState(1);
  const [confirm, setConfirm] = useState<UserListItem | null>(null);
  const pageSize = 50;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => setPage(1), [debouncedQ, role, active]);

  const users = useQuery({
    queryKey: ['users', { q: debouncedQ, role, active, page }],
    queryFn: () =>
      api<Paginated<UserListItem>>(`/api/v1/users${qs({ q: debouncedQ, role, active, page, pageSize })}`),
    placeholderData: keepPreviousData,
  });

  async function setUserActive(u: UserListItem, isActive: boolean) {
    await send('PATCH', `/api/v1/users/${u.id}`, { isActive });
    await qc.invalidateQueries({ queryKey: ['users'] });
    toast({
      tone: isActive ? 'success' : 'info',
      text: `${u.name} ${isActive ? 'diaktifkan kembali' : 'dinonaktifkan; sesinya berakhir dalam 30 detik'}.`,
    });
  }

  const data = users.data;
  return (
    <section className="fn-card" style={{ overflow: 'hidden' }}>
      <div className="fn-toolbar">
        <div className="fn-input-group" style={{ flex: 1, maxWidth: 300, minWidth: 160 }}>
          <Search className="i" aria-hidden />
          <input
            className="fn-input"
            aria-label="Cari pengguna"
            placeholder="Cari nama, username, email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          className="fn-input"
          aria-label="Peran"
          style={{ width: 'auto' }}
          value={role}
          onChange={(e) => setRole(e.target.value)}
        >
          <option value="">Semua peran</option>
          {ROLE_CODES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABEL[r]}
            </option>
          ))}
        </select>
        <select
          className="fn-input"
          aria-label="Status"
          style={{ width: 'auto' }}
          value={active}
          onChange={(e) => setActive(e.target.value)}
        >
          <option value="">Semua status</option>
          <option value="1">Aktif</option>
          <option value="0">Nonaktif</option>
        </select>
        <span className="fn-toolbar__spacer" />
        <span className="fn-hint">Profil dan role diatur di Speedmax ID, disinkronkan saat login.</span>
      </div>
      {data && data.total === 0 ? (
        <div style={{ padding: 20 }}>
          <EmptyState
            icon={<Users className="i i--xl" aria-hidden />}
            title="Tidak ada pengguna yang cocok"
            text="Pengguna muncul di sini setelah pertama kali masuk lewat Speedmax ID."
          />
        </div>
      ) : (
        <div className="fn-table-wrap">
          <table className="fn-table">
            <thead>
              <tr>
                <th>Pengguna</th>
                <th>Peran</th>
                <th>Login terakhir</th>
                <th>Terdaftar</th>
                <th className="is-end">Aktif</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((u) => (
                <tr key={u.id} style={{ opacity: u.isActive ? 1 : 0.6 }}>
                  <td>
                    <div className="fn-asset">
                      <span className={`fn-avatar${u.isActive ? '' : ' fn-avatar--neutral'}`}>
                        {initials(u.name)}
                      </span>
                      <div>
                        <div className="fn-asset__name">
                          {u.name} {u.id === me.user.id && <Badge square>Anda</Badge>}
                        </div>
                        <div className="fn-asset__code">
                          @{u.username}
                          {u.email ? ` · ${u.email}` : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1.5">
                      {u.grants.length === 0 && <span className="fn-hint">Tanpa peran</span>}
                      {u.grants.map((g) =>
                        g.area ? (
                          <RegionChip
                            key={g.ssoRoleCode}
                            code={g.area}
                            label={`Teknisi ${areaName(g.area)}`}
                          />
                        ) : (
                          <Badge key={g.ssoRoleCode} tone={g.role === 'admin' ? 'primary' : 'neutral'}>
                            {ROLE_LABEL[g.role]}
                          </Badge>
                        ),
                      )}
                    </div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {u.lastLoginAt ? dateTime.format(new Date(u.lastLoginAt)) : '—'}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{dateTime.format(new Date(u.createdAt))}</td>
                  <td className="is-end">
                    <Switch
                      label={u.isActive ? `Nonaktifkan ${u.name}` : `Aktifkan ${u.name}`}
                      checked={u.isActive}
                      disabled={u.id === me.user.id}
                      onChange={(v) =>
                        v
                          ? void setUserActive(u, true).catch((e: Error) =>
                              toast({ tone: 'danger', text: e.message }),
                            )
                          : setConfirm(u)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pager total={data?.total ?? 0} unit="pengguna" page={page} pageSize={pageSize} onPage={setPage} />
      {confirm && (
        <ConfirmDialog
          title={`Nonaktifkan ${confirm.name}?`}
          icon={<UserX className="i" aria-hidden />}
          confirmLabel="Nonaktifkan"
          onConfirm={async () => {
            await setUserActive(confirm, false);
            setConfirm(null);
          }}
          onClose={() => setConfirm(null)}
        >
          <p>
            Sesi yang sedang berjalan berakhir paling lambat 30 detik, dan pengguna tidak bisa masuk lagi
            sampai diaktifkan kembali. Akun Speedmax ID-nya tidak terpengaruh.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

// ------------------------------------------------------------------ SSO role mapping

function MappingsTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const mappings = useQuery({
    queryKey: ['sso-mappings'],
    queryFn: () => api<SsoMappingView[]>('/api/v1/sso-mappings'),
  });
  const [editing, setEditing] = useState<SsoMappingView | 'new' | null>(null);
  const [removing, setRemoving] = useState<SsoMappingView | null>(null);

  return (
    <section className="fn-card" style={{ overflow: 'hidden' }}>
      <div className="fn-card__header">
        <div>
          <h3 className="fn-card__title">Role Speedmax ID → peran FTTH NMS</h3>
          <p className="fn-card__desc">
            Kode role di SSO dipetakan ke peran dan wilayah di sini. Wilayah baru: buat role teknisi di
            Speedmax ID dan wilayah dengan kode yang sama di Pengaturan, pemetaannya dibuat otomatis.
          </p>
        </div>
        <Button
          size="sm"
          variant="primary"
          icon={<Plus className="i" aria-hidden />}
          onClick={() => setEditing('new')}
        >
          Tambah pemetaan
        </Button>
      </div>
      <div className="fn-table-wrap">
        <table className="fn-table">
          <thead>
            <tr>
              <th>Kode role SSO</th>
              <th>Peran</th>
              <th>Wilayah</th>
              <th className="is-num">Pengguna</th>
              <th className="is-end">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {mappings.data?.map((m) => {
              const locked = m.ssoRoleCode === 'administrator';
              return (
                <tr key={m.ssoRoleCode}>
                  <td className="mono">{m.ssoRoleCode}</td>
                  <td>{ROLE_LABEL[m.role]}</td>
                  <td>
                    {m.area ? <RegionChip code={m.area} /> : <span className="fn-hint">Semua wilayah</span>}
                  </td>
                  <td className="is-num">{m.userCount}</td>
                  <td className="is-end">
                    {locked ? (
                      <span className="fn-hint" title="Dikunci agar Admin tidak terkunci dari aplikasi">
                        <Lock
                          className="i i--sm"
                          aria-hidden
                          style={{ display: 'inline', verticalAlign: '-2px' }}
                        />{' '}
                        terkunci
                      </span>
                    ) : (
                      <div className="fn-table__actions">
                        <button
                          type="button"
                          className="fn-icon-btn fn-icon-btn--sm"
                          aria-label={`Ubah ${m.ssoRoleCode}`}
                          onClick={() => setEditing(m)}
                        >
                          <Pencil className="i i--sm" aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="fn-icon-btn fn-icon-btn--sm fn-icon-btn--danger"
                          aria-label={`Hapus ${m.ssoRoleCode}`}
                          onClick={() => setRemoving(m)}
                        >
                          <Trash2 className="i i--sm" aria-hidden />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && (
        <MappingDialog mapping={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
      {removing && (
        <ConfirmDialog
          title={`Hapus pemetaan ${removing.ssoRoleCode}?`}
          icon={<Trash2 className="i" aria-hidden />}
          confirmLabel="Hapus"
          onConfirm={async () => {
            await send('DELETE', `/api/v1/sso-mappings/${encodeURIComponent(removing.ssoRoleCode)}`);
            await qc.invalidateQueries({ queryKey: ['sso-mappings'] });
            toast({ tone: 'success', text: `Pemetaan ${removing.ssoRoleCode} dihapus.` });
            setRemoving(null);
          }}
          onClose={() => setRemoving(null)}
        >
          <p>
            {removing.userCount > 0
              ? `${removing.userCount} pengguna memakai role ini. Mereka kehilangan peran ini saat login berikutnya.`
              : 'Belum ada pengguna yang memakai role ini.'}
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

function MappingDialog({ mapping, onClose }: { mapping: SsoMappingView | null; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState<SsoMappingInput>({
    ssoRoleCode: mapping?.ssoRoleCode ?? '',
    role: mapping?.role ?? 'teknisi',
    area: mapping?.area ?? null,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const body = { ...v, area: v.role === 'teknisi' ? v.area : null };
      if (mapping) await send('PUT', `/api/v1/sso-mappings/${encodeURIComponent(mapping.ssoRoleCode)}`, body);
      else await send('POST', '/api/v1/sso-mappings', body);
      await qc.invalidateQueries({ queryKey: ['sso-mappings'] });
      toast({
        tone: 'success',
        text: `Pemetaan ${body.ssoRoleCode} tersimpan. Berlaku saat pengguna login berikutnya.`,
      });
      ref.current?.close();
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, undefined, (err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  const fe = error?.fieldErrors ?? {};
  return (
    <dialog ref={ref} className="fn-dialog" onClose={onClose} aria-labelledby="mapping-title">
      <form
        className="fn-panel"
        style={{
          background: 'var(--bg-surface)',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          width: 440,
          maxWidth: '100%',
        }}
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <h2 id="mapping-title" className="fn-card__title" style={{ fontSize: 15 }}>
          {mapping ? 'Ubah pemetaan role' : 'Tambah pemetaan role'}
        </h2>
        <div className="fn-form-grid">
          <Field
            label="Kode role SSO"
            required
            wide
            error={fe.ssoRoleCode}
            hint="Sama persis dengan roleCode di Speedmax ID, mis. cipongkor"
          >
            <input
              className="fn-input fn-input--mono"
              value={v.ssoRoleCode}
              onChange={(e) => setV({ ...v, ssoRoleCode: e.target.value })}
              required
            />
          </Field>
          <Field label="Peran" required error={fe.role}>
            <select
              className="fn-input"
              value={v.role}
              onChange={(e) => setV({ ...v, role: e.target.value as RoleCode })}
            >
              {ROLE_CODES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Wilayah"
            required={v.role === 'teknisi'}
            error={fe.area}
            hint={v.role === 'teknisi' ? undefined : 'Peran ini melihat semua wilayah'}
          >
            <select
              className="fn-input"
              value={v.role === 'teknisi' ? (v.area ?? '') : ''}
              disabled={v.role !== 'teknisi'}
              onChange={(e) => setV({ ...v, area: e.target.value || null })}
            >
              <option value="">{v.role === 'teknisi' ? 'Pilih wilayah' : 'Semua wilayah'}</option>
              {areaList().map((a) => (
                <option key={a.code} value={a.code}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {error && !Object.keys(fe).length && (
          <div className="fn-alert fn-alert--danger" role="alert">
            <div className="fn-alert__body">{error.message}</div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" type="button" onClick={() => ref.current?.close()} disabled={busy}>
            Batal
          </Button>
          <Button variant="primary" type="submit" loading={busy}>
            Simpan
          </Button>
        </div>
      </form>
    </dialog>
  );
}

// ------------------------------------------------------------------ permission matrix

interface MatrixView {
  catalog: PermissionDef[];
  matrix: RoleMatrix;
  defaults: Record<string, string[]>;
  editableRoles: RoleCode[];
}

function MatrixTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const view = useQuery({
    queryKey: ['role-matrix'],
    queryFn: () => api<MatrixView>('/api/v1/roles/permissions'),
  });
  const [draft, setDraft] = useState<Record<string, Set<string>> | null>(null);
  const [busy, setBusy] = useState(false);

  const current = useMemo(() => {
    const m: Record<string, Set<string>> = {};
    for (const r of view.data?.editableRoles ?? []) m[r] = new Set(view.data?.matrix[r] ?? []);
    return m;
  }, [view.data]);
  const state = draft ?? current;
  const changedRoles = (view.data?.editableRoles ?? []).filter((r) => !sameSet(state[r], current[r]));
  const groups = useMemo(() => {
    const g = new Map<string, PermissionDef[]>();
    for (const p of view.data?.catalog ?? []) g.set(p.group, [...(g.get(p.group) ?? []), p]);
    return [...g.entries()];
  }, [view.data]);

  function toggle(role: string, code: string, on: boolean) {
    const next: Record<string, Set<string>> = {};
    for (const [r, s] of Object.entries(state)) next[r] = new Set(s);
    if (on) next[role]!.add(code);
    else next[role]!.delete(code);
    setDraft(next);
  }

  async function save() {
    setBusy(true);
    try {
      for (const r of changedRoles)
        await send('PUT', `/api/v1/roles/${r}/permissions`, { permissions: [...state[r]!] });
      await qc.invalidateQueries({ queryKey: ['role-matrix'] });
      await qc.invalidateQueries({ queryKey: ['me'] });
      setDraft(null);
      toast({
        tone: 'success',
        text: `Hak akses ${changedRoles.map((r) => ROLE_LABEL[r]).join(', ')} tersimpan. Berlaku untuk semua pengguna dalam 30 detik.`,
      });
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  function restoreDefaults() {
    const next: Record<string, Set<string>> = {};
    for (const r of view.data?.editableRoles ?? []) next[r] = new Set(view.data?.defaults[r] ?? []);
    setDraft(next);
  }

  const roles: RoleCode[] = ['admin', ...(view.data?.editableRoles ?? [])];
  return (
    <section className="fn-card" style={{ overflow: 'hidden' }}>
      <div className="fn-card__header">
        <div>
          <h3 className="fn-card__title">Hak akses per peran</h3>
          <p className="fn-card__desc">
            Admin selalu punya semua hak. Hak Teknisi otomatis hanya berlaku di wilayahnya sendiri.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            icon={<RotateCcw className="i" aria-hidden />}
            onClick={restoreDefaults}
          >
            Kembalikan default
          </Button>
          <Button
            size="sm"
            variant="primary"
            icon={<KeyRound className="i" aria-hidden />}
            disabled={changedRoles.length === 0}
            loading={busy}
            onClick={() => void save()}
          >
            Simpan{changedRoles.length > 0 ? ` (${changedRoles.length} peran)` : ''}
          </Button>
        </div>
      </div>
      <div className="fn-table-wrap">
        <table className="fn-table">
          <thead>
            <tr>
              <th>Hak akses</th>
              {roles.map((r) => (
                <th key={r} style={{ textAlign: 'center', width: 96 }}>
                  {ROLE_LABEL[r]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map(([group, perms]) => (
              <GroupRows
                key={group}
                group={group}
                perms={perms}
                roles={roles}
                state={state}
                onToggle={toggle}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function GroupRows({
  group,
  perms,
  roles,
  state,
  onToggle,
}: {
  group: string;
  perms: PermissionDef[];
  roles: RoleCode[];
  state: Record<string, Set<string>>;
  onToggle: (role: string, code: string, on: boolean) => void;
}) {
  return (
    <>
      <tr>
        <td colSpan={roles.length + 1} className="fn-label" style={{ background: 'var(--bg-surface-inset)' }}>
          {group}
        </td>
      </tr>
      {perms.map((p) => (
        <tr key={p.code}>
          <td>
            <div>{p.label}</div>
            <div className="mono fn-hint">{p.code}</div>
          </td>
          {roles.map((r) => (
            <td key={r} style={{ textAlign: 'center' }}>
              <input
                type="checkbox"
                aria-label={`${p.label} untuk ${ROLE_LABEL[r]}`}
                checked={r === 'admin' || !!state[r]?.has(p.code)}
                disabled={r === 'admin'}
                onChange={(e) => onToggle(r, p.code, e.target.checked)}
                style={{
                  width: 16,
                  height: 16,
                  accentColor: 'var(--primary)',
                  cursor: r === 'admin' ? 'not-allowed' : 'pointer',
                }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function sameSet(a: Set<string> | undefined, b: Set<string> | undefined): boolean {
  if (!a || !b) return a === b;
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}
